import type {
  StorefrontCatalogProductPayloadV1,
  StorefrontEncryptedCatalogRecord,
  StorefrontEncryptedAvailabilityRecord,
  StorefrontColorAvailability,
  StorefrontProductImageDescriptor,
} from '@stokmerter/contracts';

const decoder = new TextDecoder();
const encoder = new TextEncoder();

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = `${normalized}${'='.repeat((4 - normalized.length % 4) % 4)}`;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

async function decryptCombined(
  value: string | Uint8Array<ArrayBuffer>,
  key: CryptoKey,
  additionalData: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  const combined = typeof value === 'string' ? fromBase64(value) : value;
  if (combined.byteLength < 29) throw new Error('INVALID_SEALED_DATA');
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: combined.slice(0, 12),
    additionalData,
    tagLength: 128,
  }, key, combined.slice(12));
  return new Uint8Array(plaintext);
}

function validPayload(value: unknown): value is StorefrontCatalogProductPayloadV1 {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Partial<StorefrontCatalogProductPayloadV1>;
  return payload.version === 1 &&
    typeof payload.productCode === 'string' && payload.productCode.length <= 160 &&
    typeof payload.productName === 'string' && payload.productName.length <= 240 &&
    (payload.category === null || typeof payload.category === 'string') &&
    Number.isSafeInteger(payload.seriesCount) && Number(payload.seriesCount) > 0 &&
    (payload.priceTlMinor === null || Number.isSafeInteger(payload.priceTlMinor)) &&
    (payload.priceUsdMinor === null || Number.isSafeInteger(payload.priceUsdMinor)) &&
    Array.isArray(payload.images) && payload.images.length <= 10 &&
    payload.images.every((image) => image && image.version === 1 &&
      /^sfi_[A-Za-z0-9_-]{20,80}$/u.test(image.imageId) && image.mimeType === 'image/jpeg' &&
      Number.isSafeInteger(image.width) && image.width > 0 && image.width <= 2_000 &&
      Number.isSafeInteger(image.height) && image.height > 0 && image.height <= 2_000 &&
      Number.isSafeInteger(image.ciphertextSize) && image.ciphertextSize >= 29 &&
      image.ciphertextSize <= 3 * 1_024 * 1_024 + 28 &&
      Number.isSafeInteger(image.keyVersion) && image.keyVersion >= 1 &&
      typeof image.wrappedDek === 'string' &&
      (image.role === undefined || image.role === 'lifestyle' || image.role === 'flat' ||
        image.role === 'colors')) &&
    Array.isArray(payload.colors) && payload.colors.length <= 100 &&
    payload.colors.every((color) => color &&
      /^sfv_[A-Za-z0-9_-]{20,80}$/u.test(color.variantId) &&
      typeof color.name === 'string' && color.name.length <= 120 &&
      (color.hex === null || /^#[0-9A-Fa-f]{6}$/u.test(color.hex)) &&
      (color.availability === 'in_stock' || color.availability === 'out_of_stock'));
}

export async function decryptCatalogRecord(input: {
  catalogKey: CryptoKey;
  record: StorefrontEncryptedCatalogRecord;
  storefrontId: string;
}): Promise<StorefrontCatalogProductPayloadV1> {
  const { catalogKey, record, storefrontId } = input;
  const dek = await decryptCombined(
    record.wrappedDek,
    catalogKey,
    encoder.encode(
      `stoksypro:storefront-product-dek:v1:${storefrontId}:${record.recordId}:${record.keyVersion}`,
    ),
  );
  const payloadKey = await crypto.subtle.importKey(
    'raw', dek, { name: 'AES-GCM' }, false, ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: fromBase64(record.nonce),
    additionalData: encoder.encode(
      `stoksypro:storefront-product:v1:${storefrontId}:${record.recordId}:1`,
    ),
    tagLength: 128,
  }, payloadKey, fromBase64(record.encryptedPayload));
  const payload = JSON.parse(decoder.decode(plaintext)) as unknown;
  if (!validPayload(payload)) throw new Error('INVALID_CATALOG_PAYLOAD');
  return payload;
}

export async function decryptAvailabilityRecord(input: {
  catalogKey: CryptoKey;
  record: StorefrontEncryptedAvailabilityRecord;
  storefrontId: string;
}): Promise<StorefrontColorAvailability> {
  const { catalogKey, record, storefrontId } = input;
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: fromBase64(record.nonce),
    additionalData: encoder.encode(
      `stoksypro:storefront-availability:v1:${storefrontId}:` +
      `${record.recordId}:${record.productId}:${record.variantId}:${record.keyVersion}`,
    ),
    tagLength: 128,
  }, catalogKey, fromBase64(record.encryptedAvailability));
  const value = decoder.decode(plaintext);
  if (value !== 'in_stock' && value !== 'out_of_stock') {
    throw new Error('INVALID_AVAILABILITY_PAYLOAD');
  }
  return value;
}

export async function decryptCatalogImage(input: {
  catalogKey: CryptoKey;
  ciphertext: Uint8Array<ArrayBuffer>;
  descriptor: StorefrontProductImageDescriptor;
  recordId: string;
  storefrontId: string;
}): Promise<Uint8Array<ArrayBuffer>> {
  const { catalogKey, ciphertext, descriptor, recordId, storefrontId } = input;
  if (ciphertext.byteLength !== descriptor.ciphertextSize) throw new Error('INVALID_IMAGE_SIZE');
  const dek = await decryptCombined(
    descriptor.wrappedDek,
    catalogKey,
    encoder.encode(
      `stoksypro:storefront-image-dek:v1:${storefrontId}:${recordId}:${descriptor.imageId}:${descriptor.keyVersion}`,
    ),
  );
  const imageKey = await crypto.subtle.importKey(
    'raw', dek, { name: 'AES-GCM' }, false, ['decrypt'],
  );
  return decryptCombined(
    ciphertext,
    imageKey,
    encoder.encode(
      `stoksypro:storefront-image:v1:${storefrontId}:${recordId}:${descriptor.imageId}:` +
      `${descriptor.mimeType}:${descriptor.width}:${descriptor.height}:${descriptor.ciphertextSize}`,
    ),
  );
}
